import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { LinkedinCapabilitiesList } from "@/components/LinkedinCapabilities/LinkedinCapabilitiesList";

import { isSalesNavigatorDisconnectedStatus } from "@/pages/Private/pages/Account/schema/unipile";
import { useRecheckLinkedinCapabilitiesMutation } from "@/pages/Private/redux/unipile/unipile.api";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { SettingsSubhead, SettingsSubBlock } from "./SettingsSection";

/**
 * Read-only "LinkedIn account status" section of the LinkedIn card. The values are derived from
 * Unipile (synced on connect); the Recheck button re-reads them live and the "Me" invalidation
 * refreshes the displayed flags. Renders as a subhead section inside the LinkedIn card so all
 * LinkedIn settings live together.
 */
export const LinkedinCapabilitiesCard: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.linkedinCapabilities.${key}`);

	const { user } = useAppSelector(authSelector);

	const [recheckCapabilities, { isLoading }] = useRecheckLinkedinCapabilitiesMutation();

	const [hasError, setHasError] = useState(false);

	const handleRecheck = async () => {
		setHasError(false);

		try {
			await recheckCapabilities().unwrap();
		} catch {
			setHasError(true);
		}
	};

	return (
		<div>
			<SettingsSubhead>{ts("title")}</SettingsSubhead>

			<SettingsSubBlock className="mt-2">
				<div className="flex flex-col gap-4">
					<LinkedinCapabilitiesList
						hasAdminRights={user?.hasAdminRights || false}
						hasLinkedinPremium={user?.hasLinkedinPremium || false}
						hasLiSn={user?.hasLiSn || false}
						liSnDisconnected={isSalesNavigatorDisconnectedStatus(user?.unipile?.status)}
					/>

					<p className="text-[12.5px] text-text-meta">{ts("description")}</p>

					<div>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							isLoading={isLoading}
							size={ButtonSize.S}
							title={ts("recheck")}
							type="button"
							onClick={handleRecheck}
						/>
					</div>

					{hasError && <p className="text-sm text-status-error">{ts("recheckFailed")}</p>}
				</div>
			</SettingsSubBlock>
		</div>
	);
};
